import { create, type StateCreator } from 'zustand';
import { type CustomerSelfCyo, type CYOProduct } from '@hungryroot/cyo';

export enum CYOState {
  New = 'new',
  Viewed = 'viewed',
  Selected = 'selected',
  Skipped = 'skipped',
}

interface CYOStore {
  // State
  customerCYO: CustomerSelfCyo | null;
  isLoading: boolean;
  error: string | null;
  isModalOpen: boolean;
  currentCyo: number | null | undefined;
  selectedProduct: CYOProduct | null;
  isHidden: boolean;
  scrolled: boolean;

  // Actions
  setCustomerCYO: (cyo: CustomerSelfCyo | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setModalOpen: (open: boolean) => void;
  setCurrentCyo: (cyo: number | null | undefined) => void;
  setSelectedProduct: (product: CYOProduct | null) => void;
  setIsHidden: (hidden: boolean) => void;
  setScrolled: (scrolled: boolean) => void;
  reset: () => void;
}

// -- single initializer so we don’t duplicate state/actions
const createCYOInitializer: StateCreator<CYOStore> = (set) => ({
  // Initial state
  customerCYO: null,
  isLoading: false,
  error: null,
  isModalOpen: false,
  currentCyo: null,
  selectedProduct: null,
  isHidden: false,
  scrolled: false,

  // Actions
  setCustomerCYO: (cyo) => set({ customerCYO: cyo }),
  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),
  setModalOpen: (open) => set({ isModalOpen: open }),
  setCurrentCyo: (cyo) => set({ currentCyo: cyo }),
  setSelectedProduct: (product) => set({ selectedProduct: product }),
  setIsHidden: (hidden) => set({ isHidden: hidden }),
  setScrolled: (scrolled) => set({ scrolled }),
  reset: () =>
    set({
      customerCYO: null,
      isLoading: false,
      error: null,
      isModalOpen: false,
      currentCyo: null,
      selectedProduct: null,
      isHidden: false,
      scrolled: false,
    }),
});

export const useCYOStore = create<CYOStore>(createCYOInitializer);

// Selectors
export const useCYOState = () =>
  useCYOStore((state) => ({
    customerCYO: state.customerCYO,
    isLoading: state.isLoading,
    error: state.error,
    isModalOpen: state.isModalOpen,
  }));

export const useCYOActions = () =>
  useCYOStore((state) => ({
    setCustomerCYO: state.setCustomerCYO,
    setLoading: state.setLoading,
    setError: state.setError,
    setModalOpen: state.setModalOpen,
    setCurrentCyo: state.setCurrentCyo,
    setSelectedProduct: state.setSelectedProduct,
    setIsHidden: state.setIsHidden,
    setScrolled: state.setScrolled,
    reset: state.reset,
  }));
